import LegalLayout from '../../components/LegalLayout';
import LegalPageChrome from '../../components/LegalPageChrome';
import MobileBackButton from '../../components/MobileBackButton';
import { LanguageProvider } from '../../context/LanguageContext';


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function PrivacyPolicy({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
    <LegalLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 relative">
        <div className="bg-white dark:bg-dark-card shadow-md rounded-lg p-6">
          <LegalPageChrome pageKey="privacy" backButton={<MobileBackButton />}>
          <div className="prose dark:prose-invert max-w-none">
            <h2>1. Introduction</h2>
            <p>
              CreoleCentric ("CreoleCentric," "we," "our," or "us") — a service operated by iTech Home Solutions, Inc.,
              a Florida corporation, which is the data controller for the purposes of this Privacy Policy — is committed
              to protecting your privacy and ensuring the security of your personal information. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our text-to-speech
              services, website at creolecentric.com, and any related applications or services (collectively, the "Services").
            </p>
            <p>
              By using our Services, you consent to the data practices described in this Privacy Policy. If you do not agree with 
              our policies and practices, please do not use our Services.
            </p>
            
            <h2>2. Information We Collect</h2>
            <h3>2.1 Information You Provide to Us</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, username, password, and profile information</li>
              <li><strong>Social Login Information:</strong> When you sign in using Facebook, Google, or GitHub, we receive your basic profile information (name, email, profile picture) as authorized by you</li>
              <li><strong>Payment Information:</strong> Billing address, payment method details (processed securely by our payment providers Stripe, PayPal, and NOWPayments)</li>
              <li><strong>Content Data:</strong> Text you submit for translation or text-to-speech conversion, voice recordings for Custom Voices (Enterprise only)</li>
              <li><strong>Communication Data:</strong> Messages, support tickets, and feedback you send to us</li>
              <li><strong>API Credentials:</strong> API keys and authentication tokens for developer accounts</li>
            </ul>
            
            <h3>2.2 Information We Collect Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Features used, time spent, frequency of use, performance metrics</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, IP address</li>
              <li><strong>Log Data:</strong> Access times, pages viewed, app crashes, system activity</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address</li>
              <li><strong>Cookie Data:</strong> Authentication tokens, preferences, session information</li>
            </ul>
            
            <h2>3. How We Use Your Information</h2>
            <h3>3.1 To Provide and Maintain Services</h3>
            <ul>
              <li>Process text-to-speech synthesis requests</li>
              <li>Perform language translation services</li>
              <li>Manage your account and authentication</li>
              <li>Process payments and manage subscriptions</li>
              <li>Track credit usage and allocations</li>
              <li>Provide customer support</li>
            </ul>
            
            <h3>3.2 To Improve Our Services</h3>
            <ul>
              <li>Analyze usage patterns and user behavior</li>
              <li>Develop new features and services</li>
              <li>Optimize performance and user experience</li>
              <li>Train and improve our AI models (using anonymized data only)</li>
              <li>Fix bugs and technical issues</li>
            </ul>
            
            <h3>3.3 To Communicate With You</h3>
            <ul>
              <li>Send service-related notifications</li>
              <li>Respond to inquiries and support requests</li>
              <li>Provide updates about new features or changes</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Alert you about security issues or account activities</li>
            </ul>
            
            <h2>4. Data Storage and Security</h2>
            <h3>4.1 Data Storage</h3>
            <p>
              Your data is stored on secure servers provided by our hosting partners:
            </p>
            <ul>
              <li><strong>Primary Infrastructure:</strong> Hetzner (Germany) - Main application and database hosting</li>
              <li><strong>Audio Storage:</strong> Hetzner S3 Object Storage - Generated audio files</li>
              <li><strong>Content Delivery:</strong> Cloudflare - Global CDN and DDoS protection</li>
              <li><strong>Backup Systems:</strong> Encrypted daily backups with 30-day retention</li>
            </ul>
            
            <h3>4.2 Security Measures</h3>
            <p>
              We implement industry-standard security measures including:
            </p>
            <ul>
              <li>SSL/TLS encryption for all data in transit</li>
              <li>Encryption at rest for sensitive data</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication requirements</li>
              <li>Secure API endpoints with rate limiting</li>
              <li>HMAC verification for webhook communications</li>
              <li>PCI-compliant payment processing</li>
            </ul>
            
            <h3>4.3 Data Retention</h3>
            <ul>
              <li><strong>Account Data:</strong> Retained for the duration of your account plus 90 days after deletion</li>
              <li><strong>Generated Audio:</strong> Stored for 30 days for registered users, 24 hours for anonymous users</li>
              <li><strong>Usage Logs:</strong> Retained for 12 months for analytics and debugging</li>
              <li><strong>Payment Records:</strong> Retained for 7 years as required by financial regulations</li>
            </ul>
            
            <h2>5. Data Sharing and Disclosure</h2>
            <h3>5.1 We Do Not Sell Your Data</h3>
            <p>
              CreoleCentric does not sell, trade, or rent your personal information to third parties.
            </p>
            
            <h3>5.2 Service Providers</h3>
            <p>
              We share data with trusted service providers who assist in our operations:
            </p>
            <ul>
              <li><strong>Payment Processors:</strong> Stripe, PayPal, NOWPayments (payment information only)</li>
              <li><strong>Infrastructure Providers:</strong> Hetzner, Cloudflare (hosting and delivery)</li>
              <li><strong>Analytics Services:</strong> Usage analytics (anonymized data only)</li>
              <li><strong>Communication Tools:</strong> Email service providers for transactional emails</li>
            </ul>
            
            <h3>5.3 Legal Requirements</h3>
            <p>
              We may disclose your information when required to:
            </p>
            <ul>
              <li>Comply with legal obligations or court orders</li>
              <li>Protect the rights, property, or safety of CreoleCentric, our users, or others</li>
              <li>Investigate fraud or security issues</li>
              <li>Enforce our Terms of Service</li>
            </ul>
            
            <h3>5.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of the 
              business assets. We will notify you of any such change and any choices you may have regarding your information.
            </p>
            
            <h2>6. Your Rights and Choices</h2>
            <h3>6.1 Access and Control</h3>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Restriction:</strong> Request limited processing of your data</li>
            </ul>
            
            <h3>6.2 Communication Preferences</h3>
            <ul>
              <li>Opt-out of marketing emails via unsubscribe links</li>
              <li>Manage notification preferences in account settings</li>
              <li>Disable cookies through browser settings (may affect functionality)</li>
            </ul>
            
            <h3>6.3 Data Export</h3>
            <p>
              You can export your data including generated audio history, usage statistics, and account information through 
              your account dashboard or by contacting support.
            </p>
            
            <h2>7. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These 
              countries may have data protection laws different from your country. We ensure appropriate safeguards are in 
              place to protect your information in accordance with this Privacy Policy.
            </p>
            <p>
              For users in the European Economic Area (EEA), we rely on:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Adequacy decisions where applicable</li>
              <li>Your explicit consent for certain transfers</li>
            </ul>
            
            <h2>8. Children's Privacy</h2>
            <p>
              Our Services are not intended for children under 18 years of age. We do not knowingly collect personal 
              information from children under 18. If we learn we have collected information from a child under 18, we 
              will delete that information promptly. Parents or guardians who believe we might have information from or 
              about a child under 18 should contact us immediately.
            </p>
            
            <h2>9. Cookies and Tracking Technologies</h2>
            <h3>9.1 Types of Cookies We Use</h3>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns (anonymized)</li>
              <li><strong>Session Cookies:</strong> Temporary cookies deleted when you close your browser</li>
            </ul>
            
            <h3>9.2 Managing Cookies</h3>
            <p>
              You can control cookies through your browser settings. Disabling certain cookies may limit functionality of our Services.
            </p>
            
            <h2>10. Social Login and Third-Party Authentication</h2>
            <h3>10.1 OAuth Providers</h3>
            <p>
              We offer social login options through Facebook, Google, and GitHub. When you choose to sign in using these services:
            </p>
            <ul>
              <li>We only access the basic profile information you authorize</li>
              <li>We do not post to your social media accounts without explicit permission</li>
              <li>We do not access your friends list or contacts</li>
              <li>You can revoke access through your social media account settings at any time</li>
              <li>Each provider's own privacy policy governs their collection and use of your data</li>
            </ul>
            
            <h3>10.2 Facebook Login</h3>
            <p>
              When using Facebook Login, we comply with Meta Platform Terms and only collect:
            </p>
            <ul>
              <li>Your public profile (name, profile picture)</li>
              <li>Email address (if you grant permission)</li>
            </ul>
            <p>
              We do not request or collect any other Facebook data. You can manage app permissions in your Facebook settings.
            </p>
            
            <h2>11. California Privacy Rights (CCPA)</h2>
            <p>
              California residents have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul>
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p>
              To exercise these rights, contact us at privacy@creolecentric.com or through your account settings.
            </p>
            
            <h2>12. European Privacy Rights (GDPR)</h2>
            <p>
              If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul>
              <li><strong>Legal Basis:</strong> We process your data based on consent, contract performance, legal obligations, or legitimate interests</li>
              <li><strong>Data Protection Officer:</strong> Contact our DPO at dpo@creolecentric.com</li>
              <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority</li>
            </ul>
            
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, 
              or regulatory reasons. We will notify you of material changes by:
            </p>
            <ul>
              <li>Posting the updated policy on our website</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending email notification for significant changes</li>
              <li>Requiring acknowledgment for material changes affecting your rights</li>
            </ul>
            
            <h2>14. Contact Information</h2>
            <p>
              For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <p>
              <strong>iTech Home Solutions, Inc.</strong> <em>(operating as CreoleCentric)</em><br />
              Email: privacy@creolecentric.com<br />
              Support: support@creolecentric.com<br />
              Address: 2220 CR-210 W, Suite 108-171, St Johns, FL 32259<br />
              Website: https://creolecentric.com
            </p>
            <p>
              <strong>Data Protection Officer:</strong><br />
              Email: dpo@creolecentric.com
            </p>
            
            <p className="text-sm text-gray-500 mt-8">
              Last updated: January 29, 2025<br />
              Version: 2.0<br />
              Effective Date: January 29, 2025
            </p>
          </div>
          </LegalPageChrome>
        </div>
      </div>
    </LegalLayout>
    </LanguageProvider>
  );
}
